/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { useId } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Column, DataRow, FormField, InputTextField, Typography } from '@tempmee/react';
import { StateCredentialFileV2 } from './StateCredentialFileV2';
import { CREDENTIAL_FIELD_WIDTH } from './CredentialFormFields';

/**
 * The Stripe-verified government ID name, or null when there is none to show.
 * A failed (`not-clear`) or manual verification carries a status but no names,
 * and renders "-" like the other empty rows — never a blank that looks like a match.
 */
const getGovernmentIdName = (legalIdentity) => {
  const name = [legalIdentity?.firstName, legalIdentity?.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');
  return name || null;
};

/**
 * CS name-change confirmation for one authorization (GOTU-4458), with the
 * professional's government ID name beside it for comparison (GOTU-8198).
 *
 * `confirmed_name` is keyed by professional + state, not by credential, so the
 * parent must render this at most once per authorization: every instance edits
 * the same object through `onChange`.
 */
export const ConfirmedNameSection = ({ editMode, confirmedName, onChange }) => {
  const idPrefix = useId();
  // Same source as the Verification tab's StripeLegalInfo; already loaded with the user.
  const legalIdentity = useSelector((state) => state.users.currentUser?.legalIdentityInformation);
  const governmentIdName = getGovernmentIdName(legalIdentity);
  const files = confirmedName?._files || [];
  const firstName = confirmedName?.first_name || '';
  const lastName = confirmedName?.last_name || '';

  const renderNameField = (label, value, key) => {
    if (!editMode) {
      return <DataRow label={label} value={value || '-'} />;
    }

    const inputId = `${idPrefix}-${key}`;
    return (
      <FormField label={label} htmlFor={inputId}>
        <InputTextField
          id={inputId}
          value={value}
          onChange={(e) => {
            const { value: v } = e.target;
            onChange((cn) => {
              cn[key] = v;
            });
          }}
        />
      </FormField>
    );
  };

  return (
    <Column gap={4} px={3} pt={8} width="100%">
      <Column gap={1}>
        <Typography variant="subtitle1" as="h4">
          Name Change Documents{' '}
          <Typography as="span" variant="body2" className="text-neutral-500">
            (Optional)
          </Typography>
        </Typography>
        <Typography variant="body2" className="text-neutral-500">
          Example: marriage certificate or court order.
        </Typography>
      </Column>

      <Column align="center">
        {!editMode && files.length === 0 ? (
          <Typography variant="body2" className="text-secondary" textAlign="center">
            No file uploaded
          </Typography>
        ) : (
          // Same column as the credential card's own upload area.
          <Box width={CREDENTIAL_FIELD_WIDTH}>
            <StateCredentialFileV2
              title="Name Change Documents"
              editMode={editMode}
              files={files}
              onFilesChange={(newFiles) => {
                onChange((cn) => {
                  cn._files = newFiles;
                });
              }}
            />
          </Box>
        )}
      </Column>

      {/* Centred under the documents, wider than the date rows so names have room. */}
      <Column align="center">
        <Column gap={2} width="75%">
          {/* Read-only in every mode: CS compares against it, never edits it (GOTU-8198). */}
          <DataRow label="Government ID Name" value={governmentIdName || '-'} />
          {renderNameField('First Name', firstName, 'first_name')}
          {renderNameField('Last Name', lastName, 'last_name')}
        </Column>
      </Column>
    </Column>
  );
};

ConfirmedNameSection.propTypes = {
  editMode: PropTypes.bool.isRequired,
  confirmedName: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    _files: PropTypes.arrayOf(
      PropTypes.shape({ url: PropTypes.string, file_id: PropTypes.string }),
    ),
  }),
  /** Receives an updater `(confirmedName) => void` that mutates a draft copy. */
  onChange: PropTypes.func.isRequired,
};

ConfirmedNameSection.defaultProps = {
  confirmedName: null,
};
